import { useReactiveVar } from '@apollo/client';
import { useLocation } from '@hulu/react-router-dom';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { adminGraphqlClient } from '../../apis/graphql/adminGraphqlAPI';
import {
  FEATURE_FLAGS,
  FeatureFlagsQueryResult,
  FeatureFlagsQueryVariables,
} from '../../apis/graphql/featureFlags/queries/featureFlags';
import { graphqlClient } from '../../apis/graphql/graphqlAPI';
import { selectedOrganizationIdVar, userAmsUserIdVar } from '../../constants';
import { isAuthenticatedAdminRoute } from '../LaunchDarkly/isAuthenticatedAdminRoute';
import { buildFeatureFlagMap } from './parseFeatureFlagValue';
import { type FeatureFlagsFetchOptions, refetchFeatureFlags, registerFeatureFlagsRefetch } from './refetchFeatureFlags';

const FEATURE_FLAGS_POLL_INTERVAL_MS = 60_000;

export type FeatureFlagsContextValue = {
  flags: Record<string, unknown>;
  isReady: boolean;
  loading: boolean;
  error: Error | null;
  getFlagValue: <T>(flagKey: string, defaultValue: T) => T;
};

const defaultContextValue: FeatureFlagsContextValue = {
  flags: {},
  isReady: false,
  loading: true,
  error: null,
  getFlagValue: <T,>(_flagKey: string, defaultValue: T) => defaultValue,
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(defaultContextValue);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, unknown>>({});
  const [isReady, setIsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const hasFetchedOnceRef = useRef(false);

  const userAmsUserId = useReactiveVar(userAmsUserIdVar);
  const selectedOrganizationId = useReactiveVar(selectedOrganizationIdVar);
  const location = useLocation();
  const isAdminRoute = isAuthenticatedAdminRoute(location.pathname);

  const fetchFlags = useCallback(
    async (options?: FeatureFlagsFetchOptions): Promise<void> => {
      if (!options?.background) {
        setLoading(true);
      }

      try {
        const client = isAdminRoute ? adminGraphqlClient : graphqlClient;
        const result = await client.query<FeatureFlagsQueryResult, FeatureFlagsQueryVariables>({
          query: FEATURE_FLAGS,
          variables: {
            organizationId: selectedOrganizationId ?? null,
          },
          fetchPolicy: 'network-only',
        });

        setFlags(buildFeatureFlagMap(result.data.featureFlags.flags));
        setError(null);
        setIsReady(true);
      } catch (fetchError) {
        console.error('Failed to fetch feature flags:', fetchError);
        setError(fetchError instanceof Error ? fetchError : new Error('Failed to fetch feature flags'));
        // Match LD SDK behavior: become ready on failure so consumers use default values
        // instead of staying in loading state indefinitely.
        setIsReady(true);
      } finally {
        if (!options?.background) {
          setLoading(false);
        }
      }
    },
    [isAdminRoute, selectedOrganizationId]
  );

  useEffect(() => {
    void fetchFlags({ background: hasFetchedOnceRef.current });
    hasFetchedOnceRef.current = true;
  }, [fetchFlags, userAmsUserId, selectedOrganizationId, isAdminRoute]);

  useEffect(() => {
    registerFeatureFlagsRefetch(fetchFlags);

    return () => {
      registerFeatureFlagsRefetch(null);
    };
  }, [fetchFlags]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refetchFeatureFlags({ background: true });
    }, FEATURE_FLAGS_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const getFlagValue = useCallback(
    <T,>(flagKey: string, defaultValue: T): T => {
      if (!(flagKey in flags)) {
        return defaultValue;
      }

      const value = flags[flagKey];

      if (value === null || value === undefined) {
        return defaultValue;
      }

      return value as T;
    },
    [flags]
  );

  const contextValue = useMemo(
    () => ({
      flags,
      isReady,
      loading,
      error,
      getFlagValue,
    }),
    [flags, isReady, loading, error, getFlagValue]
  );

  return <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlagsContext = (): FeatureFlagsContextValue => useContext(FeatureFlagsContext);
